/**
 * Busca inteligente sobre uma lista que já está na tela — lógica pura, sem React.
 *
 * Formas de busca (acento e caixa não importam, nem na pergunta nem no texto):
 * - palavras soltas somam (E): todas precisam aparecer, em qualquer ordem;
 * - `"frase exata"`: tem de aparecer inteira e na ordem (aspas não fechadas
 *   valem até o fim — quem ainda está digitando a frase já vê o resultado);
 * - `-palavra` e `-"frase"` excluem a linha que as contém (`-` sozinho é ignorado);
 * - rigor `flexible` casa pedaço de palavra ("apel" acha "apelação");
 *   rigor `strict` exige a palavra inteira ("ação" não acha "ações"). O rigor
 *   vale para termos, frases e exclusões;
 * - número de processo casa com ou sem pontuação, ou só um trecho — mas só
 *   quando a pergunta é SÓ número (sem letras, sem exclusão), senão
 *   "apelação 2022" traria todo processo de 2022.
 *
 * Equivalente Angular/Java: um service stateless (ou classe utilitária estática)
 * que a tela injeta para filtrar a coleção.
 */

export type SearchStrictness = 'flexible' | 'strict';

/**
 * Sem acento (marcas combinantes U+0300–U+036F após NFKD) e sem caixa. Aspas e
 * apóstrofos tipográficos viram os retos: o teclado do iOS/macOS troca `"` por
 * `“ ”` sozinho, e o nome cadastrado pode ter vindo com qualquer um dos dois.
 */
export function normalize(text: string): string {
  return text
    .normalize('NFKD')
    .replace(/[̀-ͯ]/g, '')
    .replace(/[“”„«»]/g, '"')
    .replace(/[‘’´`]/g, "'")
    // Travessão e afins (número colado de PDF) viram hífen.
    .replace(/[‐-―−]/g, '-')
    .toLowerCase();
}

export interface ParsedQuery {
  /** Termos e frases que precisam aparecer (já normalizados). */
  required: string[];
  /** Termos e frases que excluem a linha (já normalizados). */
  excluded: string[];
}

/** Quebra a pergunta em termos/frases obrigatórios e excluídos. */
export function parseQuery(query: string): ParsedQuery {
  const required: string[] = [];
  const excluded: string[] = [];
  const text = normalize(query);
  let i = 0;

  const push = (value: string, negated: boolean) => {
    const v = value.trim();
    if (!v) return;
    (negated ? excluded : required).push(v);
  };

  while (i < text.length) {
    const ch = text[i];
    if (/\s/.test(ch)) {
      i += 1;
      continue;
    }

    let negated = false;
    if (ch === '-') {
      // Um ou mais traços na frente: exclusão. Traço solto não exclui nada.
      while (text[i] === '-') i += 1;
      if (i >= text.length || /\s/.test(text[i])) continue;
      negated = true;
    }

    if (text[i] === '"') {
      const end = text.indexOf('"', i + 1);
      const stop = end === -1 ? text.length : end;
      // Espaços internos repetidos colapsam: a frase casa com o texto normal.
      push(text.slice(i + 1, stop).replace(/\s+/g, ' '), negated);
      i = stop + 1;
      continue;
    }

    let j = i;
    while (j < text.length && !/\s/.test(text[j]) && text[j] !== '"') j += 1;
    push(text.slice(i, j), negated);
    i = j;
  }

  return { required, excluded };
}

function isWordChar(ch: string | undefined): boolean {
  return ch !== undefined && /[\p{L}\p{N}]/u.test(ch);
}

/**
 * `needle` aparece em `haystack` (ambos normalizados)? No rigor `strict`, só
 * conta ocorrência com fronteira de palavra dos dois lados. Sem regex montada a
 * partir do termo — nada de escapar caractere especial.
 */
function contains(haystack: string, needle: string, strictness: SearchStrictness): boolean {
  if (strictness === 'flexible') return haystack.includes(needle);
  const startsWord = isWordChar(needle[0]);
  const endsWord = isWordChar(needle[needle.length - 1]);
  let from = 0;
  for (;;) {
    const at = haystack.indexOf(needle, from);
    if (at === -1) return false;
    const before = haystack[at - 1];
    const after = haystack[at + needle.length];
    const leftOk = !startsWord || !isWordChar(before);
    const rightOk = !endsWord || !isWordChar(after);
    if (leftOk && rightOk) return true;
    from = at + 1;
  }
}

function matchesParsed(normalizedText: string, parsed: ParsedQuery, strictness: SearchStrictness): boolean {
  for (const term of parsed.excluded) {
    if (contains(normalizedText, term, strictness)) return false;
  }
  for (const term of parsed.required) {
    if (!contains(normalizedText, term, strictness)) return false;
  }
  return true;
}

/** O texto casa com a pergunta? Pergunta vazia casa com tudo. */
export function matchesQuery(text: string, query: string, strictness: SearchStrictness = 'flexible'): boolean {
  return matchesParsed(normalize(text), parseQuery(query), strictness);
}

const CNJ_DIGITS = 20;

interface NumberQuery {
  digits: string;
  /** A pergunta como digitada, sem espaços, quando traz ponto ou hífen. */
  punctuated: string | null;
}

/**
 * A pergunta é só número (dígitos e pontuação de número, com ou sem aspas em
 * volta), sem exclusão? Devolve o que comparar, ou `null`.
 */
function parseNumberQuery(query: string): NumberQuery | null {
  // Número CNJ completo (20 dígitos) colado com texto em volta — "Processo nº
  // 0802546-26.2025.8.20.5162", o "/RS" do eproc — casa pelos dígitos.
  const allDigits = query.replace(/\D/g, '');
  if (allDigits.length === CNJ_DIGITS) return { digits: allDigits, punctuated: null };

  const q = normalize(query).trim().replace(/^"|"$/g, '').trim();
  // A barra também é pontuação de número: o CNPJ ("39.540.777/0001-84").
  if (!/^[\d.\-/\s]+$/.test(q) || /(^|\s)-/.test(q)) return null;
  const digits = q.replace(/\D/g, '');
  if (!digits) return null;
  const compact = q.replace(/\s/g, '');
  return { digits, punctuated: /[.\-/]/.test(compact) ? compact : null };
}

/**
 * Com pontuação, a pergunta é comparada COM a pontuação — "2022.8.26" não pode
 * casar "0020228-26.2021.8.26…" atravessando os segmentos. Sem pontuação,
 * compara só os dígitos ("08074203420" acha "0807420-34.20…"). Número guardado
 * sem pontuação é comparado pelos dígitos nos dois casos.
 */
function matchesNumberQuery(processNumber: string | null | undefined, nq: NumberQuery | null): boolean {
  if (!processNumber || !nq) return false;
  // Ponto, hífen e barra se equivalem ("0807420.34" acha "0807420-34"): o que importa
  // é a fronteira entre segmentos, não qual sinal o usuário usou.
  if (nq.punctuated && /[.\-/]/.test(processNumber)) {
    return processNumber.replace(/[-/]/g, '.').includes(nq.punctuated.replace(/[-/]/g, '.'));
  }
  return processNumber.replace(/\D/g, '').includes(nq.digits);
}

/** Número de processo com pontos e hífen, sem nada, ou só um trecho. */
export function matchesProcessNumber(processNumber: string | null | undefined, query: string): boolean {
  return matchesNumberQuery(processNumber, parseNumberQuery(query));
}

/** Como extrair o texto pesquisável (e o número, quando houver) de cada item. */
export interface SmartSearchFields<T> {
  text: (item: T) => string;
  processNumber?: (item: T) => string | null | undefined;
}

/** Casamento completo de uma linha: o número (se houver) ou o texto. */
export function matchesRow(
  query: string,
  strictness: SearchStrictness,
  text: string,
  processNumber?: string | null,
): boolean {
  if (!query.trim()) return true;
  if (matchesProcessNumber(processNumber, query)) return true;
  return matchesQuery(text, query, strictness);
}

/** Filtra a coleção inteira, analisando a pergunta uma vez só. */
export function filterBySmartSearch<T>(
  items: readonly T[],
  query: string,
  strictness: SearchStrictness,
  fields: SmartSearchFields<T>,
): T[] {
  if (!query.trim()) return [...items];
  const parsed = parseQuery(query);
  const numberQuery = fields.processNumber ? parseNumberQuery(query) : null;
  return items.filter((item) => {
    if (fields.processNumber && matchesNumberQuery(fields.processNumber(item), numberQuery)) return true;
    return matchesParsed(normalize(fields.text(item)), parsed, strictness);
  });
}
